import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  IconButton,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  InputAdornment,
  TablePagination,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Lock as LockIcon,
  Add as AddIcon,
  AccountBalance as AccountBalanceIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import api from "../../utils/api";
import { toast } from "react-toastify";
import Sidebar from "./Sidebar";
import {
  confirmAlert,
  successAlert,
  errorAlert,
  loadingAlert,
  closeAlert,
} from "../../utils/swalConfig";

// Add formatter utility
const formatNaira = (amount) => {
  return `₦${Number(amount).toLocaleString()}`;
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState(""); // create, edit, password, balance, role
  const [selectedUser, setSelectedUser] = useState(null);
  const [userForm, setUserForm] = useState({
    name: "",
    email: "",
    password: "",
    phoneNumber: "",
    role: "user", // Add role field
    active: true,
  });
  const [balanceForm, setBalanceForm] = useState({
    amount: "",
    balanceType: "availableBalance",
    description: "",
    operationType: "credit", // Add operation type
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [searchType, setSearchType] = useState("email"); // 'email' or 'phone'
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState("all"); // 'all', 'active', 'inactive'

  const ROLE_OPTIONS = [
    { value: "user", label: "User" },
    { value: "admin", label: "Admin" },
    { value: "moderator", label: "Moderator" },
  ];

  const BALANCE_TYPE_OPTIONS = [
    { value: "availableBalance", label: "Available Balance" },
    { value: "bonusWallet", label: "Bonus Wallet" },
  ];

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      loadingAlert("Loading users...");
      // Add query parameter to include inactive users
      const response = await api.get("/admin/users?includeInactive=true");
      closeAlert();
      setUsers(response.data.data.users);
    } catch (err) {
      closeAlert();
      errorAlert(
        "Error!",
        err.response?.data?.message || "Failed to fetch users"
      );
      setError(err.response?.data?.message || "Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const handleCredit = async () => {
    handleDialogClose(); // Close dialog immediately
    try {
      loadingAlert("Processing credit...");
      const response = await api.post("/admin/users/credit", {
        userId: selectedUser._id,
        amount: Number(balanceForm.amount),
        balanceType: balanceForm.balanceType,
        description:
          balanceForm.description ||
          `Admin credit to ${balanceForm.balanceType}`,
      });

      if (response.data.status === "success") {
        closeAlert();
        await successAlert(
          "Success!",
          `Successfully credited ₦${Number(
            balanceForm.amount
          ).toLocaleString()} to user's ${balanceForm.balanceType}`
        );
        fetchUsers();
      }
    } catch (err) {
      closeAlert();
      errorAlert(
        "Credit Failed",
        err.response?.data?.message || "Failed to credit user"
      );
    }
  };

  const handleDebit = async () => {
    handleDialogClose(); // Close dialog immediately
    try {
      loadingAlert("Processing debit...");
      const response = await api.post("/admin/users/debit", {
        userId: selectedUser._id,
        amount: Number(balanceForm.amount),
        balanceType: balanceForm.balanceType,
        description:
          balanceForm.description ||
          `Admin debit from ${balanceForm.balanceType}`,
      });

      if (response.data.status === "success") {
        closeAlert();
        await successAlert(
          "Success!",
          `Successfully debited ₦${Number(
            balanceForm.amount
          ).toLocaleString()} from user's ${balanceForm.balanceType}`
        );
        fetchUsers();
      }
    } catch (err) {
      closeAlert();
      errorAlert(
        "Debit Failed",
        err.response?.data?.message || "Failed to debit user"
      );
    }
  };

  const handleCreateUser = async () => {
    try {
      loadingAlert("Creating user...");
      const response = await api.post("/admin/users", userForm);
      closeAlert();
      await successAlert("Success!", "User created successfully");
      setUsers([...users, response.data.data.user]);
    } catch (err) {
      closeAlert();
      errorAlert(
        "Error!",
        err.response?.data?.message || "Failed to create user"
      );
    }
  };

  const handleUpdateUser = async () => {
    try {
      loadingAlert("Updating user...");
      await api.patch(`/admin/users/${selectedUser._id}`, userForm);
      closeAlert();
      await successAlert("Success!", "User updated successfully");
      await fetchUsers();
    } catch (err) {
      closeAlert();
      errorAlert(
        "Error!",
        err.response?.data?.message || "Failed to update user"
      );
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      const result = await confirmAlert(
        "Delete User",
        "Are you sure you want to delete this user?",
        "warning"
      );

      if (result.isConfirmed) {
        loadingAlert("Deleting user...");
        const token = localStorage.getItem("jwt");
        await api.delete(`/admin/users/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        closeAlert();
        await successAlert("Deleted!", "User has been deleted successfully.");
        fetchUsers(); // Refresh user list
      }
    } catch (err) {
      closeAlert();
      errorAlert(
        "Error!",
        err.response?.data?.message || "Failed to delete user"
      );
    }
  };

  // Removed unused handleChangePassword function

  const handlePasswordChange = async (userId, newPassword) => {
    try {
      const token = localStorage.getItem("jwt");
      await api.patch(
        `/admin/users/${userId}/password`,
        { password: newPassword },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success("Password updated successfully");
      handleDialogClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update password");
    }
  };

  const resetForms = () => {
    setUserForm({
      name: "",
      email: "",
      password: "",
      phoneNumber: "",
      role: "user", // Add role field
      active: true,
    });
    setSelectedUser(null);
  };

  const handleDialogOpen = (type, user = null) => {
    setDialogType(type);
    setSelectedUser(user);
    if (user && (type === "edit" || type === "role")) {
      setUserForm({
        name: user.name,
        email: user.email,
        phoneNumber: user.phoneNumber,
        role: user.role,
        active: user.active,
      });
    }
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    setUserForm({
      name: "",
      email: "",
      password: "",
      phoneNumber: "",
      role: "user", // Add role field
      active: true,
    });
    setBalanceForm({
      amount: "",
      balanceType: "availableBalance", // Default to availableBalance
      description: "",
      operationType: "credit", // Add operation type
    });
    setError(null);
  };

  const handleSubmit = async () => {
    handleDialogClose(); // Close dialog immediately
    try {
      switch (dialogType) {
        case "balance":
          if (balanceForm.operationType === "credit") {
            await handleCredit();
          } else {
            await handleDebit();
          }
          break;
        case "create":
          await handleCreateUser();
          break;
        case "edit":
          await handleUpdateUser();
          break;
        case "password":
          await handlePasswordChange(selectedUser._id, userForm.password);
          break;
        default:
          toast.error("Unknown dialog type");
          break;
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Operation failed");
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusChange = async (userId, newStatus, userRole) => {
    try {
      // Prevent deactivating admin accounts
      if (userRole === "admin" && !newStatus) {
        errorAlert("Error!", "Admin accounts cannot be deactivated");
        return;
      }

      const result = await confirmAlert(
        newStatus ? "Activate Account" : "Deactivate Account",
        `Are you sure you want to ${
          newStatus ? "activate" : "deactivate"
        } this user account?`,
        "warning"
      );

      if (result.isConfirmed) {
        loadingAlert(
          `${newStatus ? "Activating" : "Deactivating"} user account...`
        );

        await api.patch(`/admin/users/${userId}/status`, {
          active: newStatus,
        });

        // Update users list immediately for UI feedback
        setUsers(
          users.map((user) =>
            user._id === userId ? { ...user, active: newStatus } : user
          )
        );

        closeAlert();
        await successAlert(
          "Success!",
          `User account ${newStatus ? "activated" : "deactivated"} successfully`
        );
      }
    } catch (err) {
      closeAlert();
      errorAlert(
        "Error!",
        err.response?.data?.message || "Failed to update user status"
      );
    }
  };

  const filteredUsers = users.filter((user) => {
    if (!searchTerm && statusFilter === "all") return true;

    let matchesSearch = true;
    let matchesStatus = true;

    if (searchTerm) {
      switch (searchType) {
        case "email":
          matchesSearch = user.email
            .toLowerCase()
            .includes(searchTerm.toLowerCase());
          break;
        case "phone":
          matchesSearch = user.phoneNumber.includes(searchTerm);
          break;
        default:
          matchesSearch = true;
      }
    }

    if (statusFilter !== "all") {
      matchesStatus = statusFilter === "active" ? user.active : !user.active;
    }

    return matchesSearch && matchesStatus;
  });

  const paginatedUsers = filteredUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ display: "flex" }}>
      <Sidebar />
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Typography variant="h4" gutterBottom>
          User Management ({filteredUsers.length})
        </Typography>

        <Box sx={{ mb: 3, display: "flex", gap: 2, alignItems: "center" }}>
          <TextField
            sx={{ minWidth: 300 }}
            size="small"
            variant="outlined"
            placeholder={`Search by ${searchType}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton onClick={() => setSearchTerm("")} size="small">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Select
            size="small"
            value={searchType}
            onChange={(e) => setSearchType(e.target.value)}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="email">Email</MenuItem>
            <MenuItem value="phone">Phone Number</MenuItem>
          </Select>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              size="small"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All Users</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleDialogOpen("create")}
          >
            Create User
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Available Balance</TableCell>
                <TableCell>Bonus Wallet</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedUsers.map((user) => (
                <TableRow key={user._id}>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.phoneNumber}</TableCell>
                  <TableCell>{formatNaira(user.availableBalance)}</TableCell>
                  <TableCell>{formatNaira(user.bonusWallet)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Switch
                        checked={user.active}
                        onChange={() =>
                          handleStatusChange(user._id, !user.active, user.role)
                        }
                        color="success"
                        disabled={user.role === "admin"} // Disable switch for admin accounts
                      />
                      <Typography
                        variant="body2"
                        color={user.active ? "success.main" : "text.disabled"}
                      >
                        {user.active ? "Active" : "Inactive"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Edit User">
                      <IconButton
                        onClick={() => handleDialogOpen("edit", user)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Change Password">
                      <IconButton
                        onClick={() => handleDialogOpen("password", user)}
                        size="small"
                      >
                        <LockIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete User">
                      <IconButton
                        onClick={() => handleDeleteUser(user._id)}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleDialogOpen("balance", user)}
                    >
                      Manage Balance
                    </Button>
                    <Tooltip title="Change Role">
                      <IconButton
                        onClick={() => handleDialogOpen("role", user)}
                        size="small"
                      >
                        <PersonIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredUsers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>

        <Dialog open={openDialog} onClose={handleDialogClose}>
          <DialogTitle>
            {dialogType === "create"
              ? "Create New User"
              : dialogType === "edit"
              ? "Edit User"
              : dialogType === "password"
              ? "Change Password"
              : dialogType === "role"
              ? "Change Role"
              : "Manage Balance"}
          </DialogTitle>
          <DialogContent>
            {["create", "edit", "role"].includes(dialogType) && (
              <>
                <TextField
                  fullWidth
                  label="Name"
                  value={userForm.name}
                  onChange={(e) =>
                    setUserForm({ ...userForm, name: e.target.value })
                  }
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={userForm.email}
                  onChange={(e) =>
                    setUserForm({ ...userForm, email: e.target.value })
                  }
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={userForm.phoneNumber}
                  onChange={(e) =>
                    setUserForm({ ...userForm, phoneNumber: e.target.value })
                  }
                  margin="normal"
                />
                {dialogType === "create" && (
                  <TextField
                    fullWidth
                    label="Password"
                    type="password"
                    value={userForm.password}
                    onChange={(e) =>
                      setUserForm({ ...userForm, password: e.target.value })
                    }
                    margin="normal"
                  />
                )}
                <FormControl fullWidth margin="normal">
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={userForm.role}
                    onChange={(e) =>
                      setUserForm({ ...userForm, role: e.target.value })
                    }
                  >
                    {ROLE_OPTIONS.map((role) => (
                      <MenuItem key={role.value} value={role.value}>
                        {role.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
            {dialogType === "password" && (
              <TextField
                fullWidth
                label="New Password"
                type="password"
                value={userForm.password}
                onChange={(e) =>
                  setUserForm({ ...userForm, password: e.target.value })
                }
                margin="normal"
              />
            )}
            {dialogType === "balance" && (
              <>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Operation Type</InputLabel>
                  <Select
                    value={balanceForm.operationType}
                    onChange={(e) =>
                      setBalanceForm({
                        ...balanceForm,
                        operationType: e.target.value,
                      })
                    }
                  >
                    <MenuItem value="credit">Credit User</MenuItem>
                    <MenuItem value="debit">Debit User</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth margin="normal">
                  <InputLabel>Balance Type</InputLabel>
                  <Select
                    value={balanceForm.balanceType}
                    onChange={(e) =>
                      setBalanceForm({
                        ...balanceForm,
                        balanceType: e.target.value,
                      })
                    }
                  >
                    {BALANCE_TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  type="number"
                  label="Amount"
                  value={balanceForm.amount}
                  onChange={(e) =>
                    setBalanceForm({
                      ...balanceForm,
                      amount: e.target.value,
                    })
                  }
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">₦</InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Description (Optional)"
                  value={balanceForm.description}
                  onChange={(e) =>
                    setBalanceForm({
                      ...balanceForm,
                      description: e.target.value,
                    })
                  }
                  margin="normal"
                />
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose}>Cancel</Button>
            {dialogType === "balance" ? (
              <>
                <Button
                  variant="contained"
                  color={
                    balanceForm.operationType === "credit" ? "success" : "error"
                  }
                  onClick={
                    balanceForm.operationType === "credit"
                      ? handleCredit
                      : handleDebit
                  }
                >
                  {balanceForm.operationType === "credit" ? "Credit" : "Debit"}{" "}
                  User
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
              >
                {dialogType === "create" ? "Create" : "Update"}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default UserManagement;
